import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import Accordion from "react-bootstrap/Accordion";
import Form from "react-bootstrap/Form";
import Nav from "./Nav";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import {
  ALLERGEN_SAFETY_PILLAR_PATH,
  ALLERGEN_SAFETY_QA_GUIDE_PATH,
  MENU_TRAINING_CHECKLIST_PATH,
  MENU_TRAINING_PILLAR_PATH,
  MENU_TRAINING_TEMPLATE_PATH,
  SERVER_TRAINING_CHECKLIST_PATH,
  SERVER_TRAINING_PILLAR_PATH,
  shouldShowContentClusterPromos,
  WINE_TRAINING_PAIRING_GUIDE_PATH,
  WINE_TRAINING_PILLAR_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";
import "../styles/faq-page.css";

// SEO-optimized FAQ data: Add new Q&A objects to this array to grow your SEO footprint
const faqData = [
  // --- Current status, access, pricing, and setup ---
  {
    question: "Is Speak Your Menu live now?",
    answer:
      "Yes. Speak Your Menu is live today. Restaurants can create an account, upload their menu, invite staff, and start training immediately. Managers use the browser-based web dashboard, and employees can train using the iOS and Android mobile apps or a mobile web browser."
  },
  {
    question: "Is there a mobile app for Speak Your Menu?",
    answer:
      "Yes. Speak Your Menu is available as native mobile apps on both the Apple App Store (iOS) and Google Play Store (Android). Employees can log in with the same credentials across web and mobile. Managers use the web dashboard for admin tools and reporting."
  },
  {
    question: "How much does Speak Your Menu cost?",
    answer:
      "Speak Your Menu has simple, restaurant-friendly pricing. The Free tier lets you explore the training experience at no cost. When you are ready to roll out full training, paid plans start with a Single Location subscription and scale up for multi-location and enterprise groups. Pricing is month-to-month with no long-term contracts, so you can grow as your restaurant group grows."
  },
  {
    question: "Is there a free plan or free trial?",
    answer:
      "Yes. Our Free plan lets you experience Speak Your Menu without a credit card. You can set up your restaurant, upload menu items, and unlock the first full unit of training in both Food and Wine. When you are ready for full-menu training, you can upgrade from inside your account with just a few clicks."
  },
  {
    question: "Do I need to sign a long-term contract?",
    answer:
      "No. Speak Your Menu is designed for flexibility. Standard subscriptions are billed month-to-month, so you are never locked into a long-term contract. You can upgrade, downgrade, or cancel as your restaurant’s needs change."
  },
  {
    question: "How do I get started with Speak Your Menu?",
    answer:
      "Visit SpeakYourMenu.com and create an account for your restaurant. Once you are in, you can upload your food and wine menu, tag allergens and restrictions, and invite your staff. Speak Your Menu turns your menu into interactive lessons, so your team can begin training right away on desktop or mobile."
  },

  // --- General learning & experience questions ---
  {
    question: "How does this app make learning the menu easier?",
    answer:
      "Speak Your Menu breaks your restaurant menu into short, interactive lessons that feel like using a language learning app. Restaurant staff learn real phrases to use with guests, not just facts. It is fast, mobile-friendly, and focused on the menu items that matter most for service and guest satisfaction."
  },
  {
    question: "Do I need to be tech-savvy to use it?",
    answer:
      "Not at all. If you have ever used an app like Duolingo or Instagram, you will feel right at home. Just log in, start your lessons, and follow the prompts. The system is designed to be intuitive for all levels of restaurant staff, from new servers to experienced managers."
  },
  {
    question: "Can I use the app on my phone?",
    answer:
      "Yes. Employees can train on iOS and Android using the native mobile apps, or through a mobile web browser. Managers use the web dashboard for menu setup, staff management, and reporting."
  },
  {
    question: "How are lessons different from boring quizzes?",
    answer:
      "We do not rely on traditional tests or flashcards. Lessons use second-language learning techniques to reinforce memory and understanding. Staff see menu phrases multiple times in different ways so they remember naturally and can speak confidently with guests instead of just memorizing a list of facts."
  },
  {
    question: "How does this app help me feel more confident when talking to guests about the menu?",
    answer:
      "Speak Your Menu teaches you how guests actually ask questions and how to respond clearly. By practicing these real-world phrases in short lessons, you will feel prepared instead of put on the spot. Confidence comes from familiarity and repetition, and that is exactly how the training is designed."
  },
  {
    question: "Will I be tested or graded?",
    answer:
      "There are no stressful grades, but progress is tracked so managers can see training activity and completion. Lessons are built to reinforce, not punish, so you keep improving without feeling judged. You can view the menu at any point during your lesson to help find the right answer and strengthen your knowledge."
  },
  {
    question: "How is Speak Your Menu different from flashcard apps?",
    answer:
      "Flashcards only test memory in one direction. Speak Your Menu focuses on menu fluency. Lessons simulate real conversations and reinforce menu terms through multiple exposure points, which is more practical for restaurant staff who need to talk confidently at the table."
  },
  {
    question: "What learning techniques does it use?",
    answer:
      "Speak Your Menu uses second language acquisition methods, spaced repetition, and active recall. These proven learning techniques improve retention and accelerate menu training for restaurant staff, even when they only have a few minutes a day to train."
  },
  {
    question: "Does this app teach foreign menu terms like Italian or French?",
    answer:
      "Not in the current MVP, but it is part of our future roadmap. We plan to include pronunciation and term guides for commonly used foreign words in food and wine, helping staff speak confidently about international dishes and wines."
  },

  // --- Beverage and wine education ---
  {
    question: "How does the app handle beverage education for wine specifically?",
    answer:
      "Speak Your Menu includes a dedicated wine training experience that teaches your team about your specific wine list using real-world, conversational phrasing. It helps staff internalize style, grape, region, and structure so they can describe wines at the table without sounding scripted or overly technical."
  },
  {
    question: "Can I upload detailed wine info like varietals, origin, and vintage?",
    answer:
      "Yes. When you add a wine in the manager dashboard, you can input brand name, wine name, varietal or blend, region, vintage, style, and more. You can also upload a bottle label image. Speak Your Menu converts this information into training lessons so your staff can recognize and talk about each wine on your list."
  },
  {
    question: "Will you support wine pairings in the future?",
    answer:
      "Yes. While the current release does not teach specific food and wine pairings, pairings are on our product roadmap. Future updates may include optional pairing education so your team can make confident recommendations."
  },
  {
    question: "Will you support other beverages like beer or cocktails in the future?",
    answer:
      "Yes. The first version of Speak Your Menu focuses on food and wine, but we plan to expand into beer, cocktails, spirits, and non-alcoholic programs. The goal is for your entire beverage program to live in one training system so staff can speak confidently about what you offer."
  },

  // --- Menu, allergens, and operations ---
  {
    question: "Why should chefs care about front-of-house menu knowledge?",
    answer:
      "Chefs and kitchen teams benefit when the front of house can accurately describe dishes, techniques, and ingredients. When servers and managers understand the menu, it reduces remakes, special requests that do not make sense, and last-minute clarifications. That keeps the kitchen focused and protects the chef’s vision on the plate."
  },
  {
    question: "Can I add ingredient details and allergy info for each dish?",
    answer:
      "Yes. Speak Your Menu is built around accurate ingredient and allergen data. Managers can add ingredient lists and tag common allergens during menu upload. That information flows into staff lessons, so teams learn which dishes contain milk, eggs, shellfish, nuts, gluten, and more."
  },
  {
    question: "How do I ensure kitchen staff are aligned with front-of-house training?",
    answer:
      "You can include back-of-house staff in Speak Your Menu so everyone trains on the same menu information. This reduces confusion about ingredients, modifications, and specials. Many restaurants use the system to support pre-shift meetings so both the kitchen and the dining room are aligned on the same menu details."
  },
  {
    question: "Will this reduce errors and modifications on orders?",
    answer:
      "Many order mistakes come from incomplete menu knowledge. When staff understand ingredients, components, and how dishes are built, they are less likely to misquote or miss key details. This can help reduce incorrect orders and improve service consistency."
  },
  {
    question: "How does the app help streamline the onboarding process?",
    answer:
      "As soon as new hires are added, they get access to structured lessons built from your actual menu. Instead of relying only on shadowing or a binder, they can train in short sessions before their first shifts. This shortens ramp-up time and gives managers visibility into training activity."
  },
  {
    question: "What kind of reports or analytics does the app provide?",
    answer:
      "The manager dashboard shows lesson completion, accuracy, and activity over time. You can see which staff are up-to-date on training and who may need coaching. For multi-location groups, you can compare performance across restaurants to spot trends in training and consistency."
  },
  {
    question: "Can I assign different lessons to different employees?",
    answer:
      "Today, Speak Your Menu automatically assigns lessons based on your menu so staff learn the full offering. More role-based lesson assignments are on our roadmap so you can tailor training for servers, bartenders, captains, and managers while still keeping everyone aligned on core menu knowledge."
  },
  {
    question: "How easy is it to update my menu in the app?",
    answer:
      "It is simple. Managers can log in to the web dashboard and update dishes, ingredients, prices, or wines in just a few clicks. Once you save, training content is refreshed so your staff can learn the newest version of the menu without waiting for reprints or new manuals."
  },
  {
    question: "How does the app handle seasonal or rotating menus?",
    answer:
      "Speak Your Menu is built for restaurants that change menus. You can add seasonal dishes, mark items as unavailable, or reactivate older items when they come back. Past items stay saved in your account so you do not need to re-enter them every season."
  },
  {
    question: "Can I manage multiple locations with one account?",
    answer:
      "Yes. Multi-location support is built in. Directors of operations and owners can manage multiple restaurants with separate menus and staff lists from a single login. You can see training data by location while maintaining a consistent training standard across the group."
  },

  // --- Revenue, guest experience, and outcomes ---
  {
    question: "Why is menu education important for increasing revenue?",
    answer:
      "Well-trained staff recommend the right dishes, confidently describe items, and avoid avoidable mistakes. This can lead to higher average check sizes, better use of high-margin items, and fewer comps. Menu education turns your team into a more consistent sales and service engine."
  },
  {
    question: "How does Speak Your Menu improve customer loyalty?",
    answer:
      "Guests notice when staff understand the menu. When servers can answer detailed questions, give thoughtful recommendations, and handle dietary needs with care, guests feel taken care of. That experience builds trust and repeat business over time."
  },
  {
    question: "How quickly can I expect staff to become fluent in our menu?",
    answer:
      "Many teams see a noticeable difference after a week or two of light, regular use. Because the lessons are short and repeat key concepts, staff can train before or after a shift and still make real progress over time."
  },
  {
    question: "What makes this different from traditional staff training or wine schools?",
    answer:
      "Traditional training is often a one-time event. Speak Your Menu is continuous, bite-sized, and menu-specific. It combines language-learning techniques with your real food and wine list, so everything staff learn is directly relevant to the shifts they work."
  },
  {
    question: "Is this app affordable compared to traditional training methods?",
    answer:
      "Printing manuals, hosting long training sessions, and fixing service mistakes all add up. Speak Your Menu offers predictable monthly pricing that gives staff access to structured training. The goal is to reduce wasted time and prevent avoidable mistakes."
  },
  {
    question: "Can I track employee progress through the app?",
    answer:
      "Yes. Managers can see who has logged in, which lessons they have completed, and how accurately they are answering questions. This insight helps coaching conversations and makes training expectations more concrete."
  },

  // --- Security, data, and account basics ---
  {
    question: "How is our restaurant data stored and protected?",
    answer:
      "Speak Your Menu is built on modern cloud infrastructure and follows standard best practices for security and data protection. Menu data and user accounts are stored in secure databases with role-based access, so staff only see what they need. We continually improve our infrastructure as we grow to keep your information safe."
  },
  {
    question: "What is Speak Your Menu in one sentence?",
    answer:
      "Speak Your Menu is a restaurant training platform that turns your actual menu into interactive, language-style lessons so staff can learn faster and serve guests with confidence."
  },
  {
    question: "Is Speak Your Menu only for full-service restaurants?",
    answer:
      "No. Speak Your Menu is designed for all types of restaurants, including full-service, fast casual, and counter service. Any hospitality business where staff need to talk about the menu can benefit from consistent, structured training."
  },
  {
    question: "Does Speak Your Menu work for both small restaurants and large groups?",
    answer:
      "Yes. Speak Your Menu works for small independent restaurants all the way up to multi-location groups and franchises. It unifies training across locations so staff receive the same menu knowledge, no matter where they work."
  },
  {
    question: "Can I use Speak Your Menu even if we don't offer a full food menu?",
    answer:
      "Absolutely. Many restaurants, wine bars, and beverage-focused concepts use Speak Your Menu. The platform supports detailed wine training and can be used even if your food menu is limited."
  },
  {
    question: "If we already do strong pre-shift meetings, do we still need Speak Your Menu?",
    answer:
      "Pre-shift meetings are valuable, but they cannot cover all the menu knowledge your team needs. Speak Your Menu reinforces menu details over time, so staff come into pre-shift better prepared and ready with higher-level questions."
  },
  {
    question: "Can Speak Your Menu help with seasonal or temporary staff?",
    answer:
      "Yes. Seasonal and temporary employees can learn your menu quickly through short lessons, allowing them to contribute faster than traditional onboarding alone."
  },
  {
    question: "Is Speak Your Menu good for restaurants with high staff turnover?",
    answer:
      "Yes. Speak Your Menu helps standardize menu training for every new hire, reducing inconsistencies and helping managers maintain a reliable training standard."
  },
  {
    question: "Is Speak Your Menu only for new hires?",
    answer:
      "Not at all. Many long-time employees never received a structured way to learn the menu. Speak Your Menu can elevate the entire team by reinforcing details and correcting gaps over time."
  },

  // ---------------------------------------
  // SECTION 2 — IMPLEMENTATION & ONBOARDING
  // ---------------------------------------
  {
    question: "How long does it take to upload my menu?",
    answer:
      "Uploading your menu is fast. You can bulk upload dishes through Excel or CSV, and staff can be bulk uploaded the same way. Larger or more complex menus may take a day or two to finalize, but the process is simple and intuitive."
  },
  {
    question: "Can Speak Your Menu help me upload my menu?",
    answer:
      "Yes. If you’d like assistance uploading your menu, simply reach out. We’re happy to support you during setup."
  },
  {
    question: "Can I start with only part of my menu?",
    answer:
      "Yes. Many restaurants begin with dinner service or the menu section that’s hardest for staff. As your team becomes confident, you can expand training to include your full menu."
  },
  {
    question: "How long does each lesson take?",
    answer:
      "Lessons are short and focused. Most take around 10 minutes to complete, making it easy for staff to learn before or after shifts or during downtime."
  },
  {
    question: "Do staff train on a specific schedule?",
    answer:
      "Staff can train whenever it works for them. Speak Your Menu includes recurring reminders every 3, 5, or 7 days depending on how often each employee works, helping them stay consistent."
  },
  {
    question: "How do I invite staff and manage their logins?",
    answer:
      "You enter each employee’s name and email into the dashboard. Speak Your Menu automatically sends an onboarding email, and staff can begin training immediately."
  },
  {
    question: "What happens when a new employee joins?",
    answer:
      "Add them to your restaurant profile. They will receive an email invitation and can start learning your menu right away."
  },

  // --------------------------------
  // SECTION 3 — PRODUCT FEATURES
  // --------------------------------
  {
    question: "Does Speak Your Menu replace pre-shift meetings?",
    answer:
      "No. Speak Your Menu does not replace pre-shift lineups or in-person communication. Instead, it strengthens them by giving staff a stronger foundation beforehand so they arrive better prepared and ask more thoughtful questions."
  },
  {
    question: "Can I upload photos of dishes and wine labels?",
    answer:
      "Yes. Visual learning is a major part of Speak Your Menu. Adding photos of dishes and bottle labels helps staff recognize items during service."
  },
  {
    question: "Does Speak Your Menu support multiple languages?",
    answer:
      "Not yet, but multi-language support is a major priority on our roadmap and will be added in a future update."
  },
  {
    question: "Can I customize the lesson questions?",
    answer:
      "At this time, lesson questions cannot be customized. However, you can choose which parts of your menu employees start with, such as food items or your wine list."
  },
  {
    question: "Does Speak Your Menu include tests or quizzes?",
    answer:
      "No. Instead of quizzes, Speak Your Menu uses second-language acquisition techniques. Staff always see the correct answer after each interaction, which builds fluency faster than traditional memorization tools."
  },
  {
    question: "Can I pause training for an employee or remove them?",
    answer:
      "Yes. Employees on leave can have their training paused, and former employees can be removed from your restaurant profile at any time."
  },
  {
    question: "Does Speak Your Menu work offline?",
    answer:
      "Not yet. The app currently requires an Internet connection. Offline access may be added in the future."
  },
  {
    question: "Can I export training data for HR or operations?",
    answer:
      "Exporting is not available yet. However, managers have access to a dashboard that shows progress, accuracy, and training completion across the team."
  },

  // -------------------------------------------
  // SECTION 4 — POS, TECH STACK & INTEGRATIONS
  // -------------------------------------------
  {
    question: "Does Speak Your Menu integrate with POS systems?",
    answer:
      "Not at this time. Speak Your Menu is currently a closed system without POS integrations, but we are open to adding integrations in the future."
  },
  {
    question: "Can Speak Your Menu automatically import my POS menu?",
    answer:
      "No. There are no third-party integrations yet, so menu items cannot be automatically pulled from POS or inventory systems. You can upload menu items quickly through CSV or direct entry."
  },
  {
    question: "Do staff need to download the app?",
    answer:
      "Employees can train in the browser or by downloading the native iOS and Android apps. The training experience is designed to work across both options."
  },
  {
    question: "Do the mobile apps include the manager dashboard?",
    answer:
      "No. The mobile apps are built for employees to take lessons. The manager dashboard is available only through the web browser to ensure full visibility and access to admin tools and metrics."
  },

  // --------------------------------------
  // SECTION 5 — PRICING, BILLING & TERMS
  // --------------------------------------
  {
    question: "How does pricing work for multiple locations?",
    answer:
      "Pricing is $129.99 for the first location and $50 for each additional location. This structure keeps training affordable as your business grows."
  },
  {
    question: "Do you offer annual discounts?",
    answer:
      "Not yet, but we are open to offering annual plans and discounts in the future. Enterprise groups may qualify for custom pricing."
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We process payments through Stripe, which accepts all major credit cards."
  },
  {
    question: "What happens if I cancel my subscription?",
    answer:
      "You can cancel or change your plan at any time. Your account remains protected, and we follow standard best practices for security and data protection."
  },
  {
    question: "Do you offer pilot programs?",
    answer:
      "Yes. We offer pilot programs for enterprise groups. Please reach out to begin the conversation."
  },

  // ---------------------------------------
  // SECTION 6 — TRAINING OUTCOMES & ROI
  // ---------------------------------------
  {
    question: "How does Speak Your Menu help increase average check size?",
    answer:
      "Speak Your Menu improves staff confidence at the table, helping them explain dishes, answer questions, and recommend items without needing to leave the guest. This supports better conversations and stronger suggestions over time."
  },
  {
    question: "Can Speak Your Menu reduce comped meals and mistakes?",
    answer:
      "It can help reduce avoidable mistakes by improving menu knowledge and consistency. When staff understand ingredients, allergens, and key details, they are less likely to make errors that lead to guest dissatisfaction."
  },
  {
    question: "How do I measure whether Speak Your Menu is working?",
    answer:
      "Your manager dashboard shows employee progress, lesson completion, accuracy, and how well each dish or wine is understood across your team. These insights make it easier to track training and identify where coaching is needed."
  },
  {
    question: "Have restaurants seen real results with Speak Your Menu?",
    answer:
      "Speak Your Menu is built around a simple idea: structured practice improves retention. Restaurants adopt it to standardize training, reduce inconsistencies, and make menu knowledge easier to maintain as staff and menus change."
  },
  {
    question: "Will Speak Your Menu help staff remember specials and seasonal items?",
    answer:
      "Yes. Because training is built from your menu and reinforced over time, it can help staff retain details about seasonal dishes and specials more consistently."
  },

  // ----------------------------------------
  // SECTION 7 — STAFF EXPERIENCE & BUY-IN
  // ----------------------------------------
  {
    question: "Will staff need to use their phones during service?",
    answer:
      "No. Staff do not need to use phones on the floor, but they can complete lessons before or after shifts. Speak Your Menu is designed to fit into short training windows outside of service."
  },
  {
    question: "How do I get my team to buy in?",
    answer:
      "Explain that Speak Your Menu has no tests or high-pressure grading. Lessons reinforce correct answers naturally, everyone learns the same information, and staff can support each other with more consistent menu knowledge on the floor."
  },
  {
    question: "Can I set expectations for how often staff should train?",
    answer:
      "Yes. Speak Your Menu sends reminders every 3, 5, or 7 days depending on how often each employee works. This keeps training consistent without overwhelming your team."
  },
  {
    question: "Is Speak Your Menu appropriate for older or less tech-savvy staff?",
    answer:
      "Absolutely. The interface is intuitive, lessons are simple, and staff of all ages find the system easy to use."
  },
  {
    question: "Do staff need their own devices?",
    answer:
      "Staff can train on their own phones, tablets, or home computers. Speak Your Menu is built mobile-first because most staff prefer learning on their phones."
  },

  // ---------------------------------------------------
  // SECTION 8 — SAFETY, ALLERGEN & DIETARY RESTRICTIONS
  // ---------------------------------------------------
  {
    question: "How does Speak Your Menu help with allergy safety?",
    answer:
      "Speak Your Menu teaches allergens as they apply to each dish and reinforces safe communication habits for allergy-related questions. This helps staff respond more consistently when guests have dietary concerns."
  },
  {
    question: "Does Speak Your Menu teach dietary restrictions like gluten-free or vegan?",
    answer:
      "Yes. Speak Your Menu trains staff on gluten-free, vegan, vegetarian, and other dietary needs. We categorize restrictions into health, lifestyle, and belief to help staff understand different requirements."
  },
  {
    question: "Does Speak Your Menu help staff answer detailed allergy questions at the table?",
    answer:
      "Yes. Staff learn which dishes contain which allergens, which items can be substituted, and how modifications impact safety. This supports more accurate answers and better guest trust."
  },
  {
    question: "How quickly can I update allergen or dietary information?",
    answer:
      "Very quickly. Managers can update allergens and restrictions directly in the dashboard, and changes are reflected in staff training."
  },

  // ------------------------------------------
  // SECTION 10 — SUPPORT, TRAINING & SUCCESS
  // ------------------------------------------
  {
    question: "Do you offer onboarding support?",
    answer:
      "Yes. We offer onboarding support and training for managers on a case-by-case basis. If you’d like help setting up your menu and training flow, reach out and we can coordinate."
  },
  {
    question: "What kind of ongoing support do you provide?",
    answer:
      "We offer a support email, and many customers also coordinate directly with us as needed. If you run into questions or issues, you can reach out anytime."
  },
  {
    question: "Do you provide best practices for rolling out Speak Your Menu?",
    answer:
      "We can help managers think through rollout strategy based on their menu and staffing patterns. If you want guidance on sequencing training or setting expectations, reach out and we can share recommendations."
  },
  {
    question: "Is the training different for new hires versus existing staff?",
    answer:
      "Training is the same for everyone because all staff benefit from standardized knowledge. Many seasoned employees never received a structured way to learn the menu, and Speak Your Menu helps reinforce those details over time."
  },

  // -------------------------------------------
  // SECTION 12 — ROLE-SPECIFIC VALUE
  // -------------------------------------------
  {
    question: "How does Speak Your Menu help general managers and directors of operations?",
    answer:
      "Managers and directors can see training progress for employees, identify who needs additional support, and track knowledge by dish, wine, or category. This provides detailed control over training at one or multiple locations."
  },
  {
    question: "How does Speak Your Menu help servers and front-of-house staff?",
    answer:
      "Speak Your Menu reinforces menu knowledge, increases confidence, and helps staff answer guest questions with more consistency. This improves service quality and supports stronger hospitality at the table."
  },
  {
    question: "How does Speak Your Menu support wine directors and beverage managers?",
    answer:
      "Our Wine Styles system teaches staff how to talk about each wine confidently. This empowers more employees to sell wine and reduces reliance on one or two beverage experts."
  },
  {
    question: "Can back-of-house, hosts, or support staff use Speak Your Menu?",
    answer:
      "Yes. Many restaurants train their kitchen teams, bussers, runners, hosts, and support staff so everyone can answer guest questions more confidently and create a more unified dining experience."
  }
  // To add more questions and answers:
  // 1. Add a new object to the faqData array above, e.g.
  //    { question: "Your question?", answer: "Your answer." }
  // 2. Save the file. The FAQ page and SEO schema will update automatically.
];

const FAQ_CATEGORIES = [
  "Getting Started",
  "Mobile Apps",
  "Pricing & Billing",
  "Training Experience",
  "Menu, Allergens & Safety",
  "Multi-Location & Operations",
  "Integrations & Tech",
  "Support & Roadmap",
];

const FAQ_CATEGORY_QUESTIONS = {
  "Getting Started": [
    "Is Speak Your Menu live now?",
    "How do I get started with Speak Your Menu?",
    "Do I need to be tech-savvy to use it?",
    "How does the app help streamline the onboarding process?",
    "What is Speak Your Menu in one sentence?",
    "Is Speak Your Menu only for full-service restaurants?",
    "Can I use Speak Your Menu even if we don't offer a full food menu?",
    "How long does it take to upload my menu?",
    "Can Speak Your Menu help me upload my menu?",
    "Can I start with only part of my menu?",
    "How do I invite staff and manage their logins?",
    "What happens when a new employee joins?",
    "Is Speak Your Menu appropriate for older or less tech-savvy staff?",
  ],
  "Mobile Apps": [
    "Is there a mobile app for Speak Your Menu?",
    "Can I use the app on my phone?",
    "Does Speak Your Menu work offline?",
    "Do staff need to download the app?",
    "Do the mobile apps include the manager dashboard?",
    "Will staff need to use their phones during service?",
    "Do staff need their own devices?",
  ],
  "Pricing & Billing": [
    "How much does Speak Your Menu cost?",
    "Is there a free plan or free trial?",
    "Do I need to sign a long-term contract?",
    "Is this app affordable compared to traditional training methods?",
    "How does pricing work for multiple locations?",
    "Do you offer annual discounts?",
    "What payment methods do you accept?",
    "What happens if I cancel my subscription?",
    "Do you offer pilot programs?",
  ],
  "Training Experience": [
    "How does this app make learning the menu easier?",
    "How are lessons different from boring quizzes?",
    "How does this app help me feel more confident when talking to guests about the menu?",
    "Will I be tested or graded?",
    "How is Speak Your Menu different from flashcard apps?",
    "What learning techniques does it use?",
    "How quickly can I expect staff to become fluent in our menu?",
    "What makes this different from traditional staff training or wine schools?",
    "If we already do strong pre-shift meetings, do we still need Speak Your Menu?",
    "How long does each lesson take?",
    "Do staff train on a specific schedule?",
    "Does Speak Your Menu replace pre-shift meetings?",
    "Can I customize the lesson questions?",
    "Does Speak Your Menu include tests or quizzes?",
    "Will Speak Your Menu help staff remember specials and seasonal items?",
    "How do I get my team to buy in?",
    "Can I set expectations for how often staff should train?",
    "Is the training different for new hires versus existing staff?",
    "How does Speak Your Menu help servers and front-of-house staff?",
    "Is Speak Your Menu only for new hires?",
  ],
  "Menu, Allergens & Safety": [
    "How does the app handle beverage education for wine specifically?",
    "Can I upload detailed wine info like varietals, origin, and vintage?",
    "Can I add ingredient details and allergy info for each dish?",
    "Will this reduce errors and modifications on orders?",
    "Can I upload photos of dishes and wine labels?",
    "How does Speak Your Menu help increase average check size?",
    "Can Speak Your Menu reduce comped meals and mistakes?",
    "How does Speak Your Menu help with allergy safety?",
    "Does Speak Your Menu teach dietary restrictions like gluten-free or vegan?",
    "Does Speak Your Menu help staff answer detailed allergy questions at the table?",
    "How quickly can I update allergen or dietary information?",
    "How does Speak Your Menu support wine directors and beverage managers?",
  ],
  "Multi-Location & Operations": [
    "Why should chefs care about front-of-house menu knowledge?",
    "How do I ensure kitchen staff are aligned with front-of-house training?",
    "What kind of reports or analytics does the app provide?",
    "Can I assign different lessons to different employees?",
    "How easy is it to update my menu in the app?",
    "How does the app handle seasonal or rotating menus?",
    "Can I manage multiple locations with one account?",
    "Why is menu education important for increasing revenue?",
    "How does Speak Your Menu improve customer loyalty?",
    "Can I track employee progress through the app?",
    "Does Speak Your Menu work for both small restaurants and large groups?",
    "Can Speak Your Menu help with seasonal or temporary staff?",
    "Is Speak Your Menu good for restaurants with high staff turnover?",
    "Can I pause training for an employee or remove them?",
    "Can I export training data for HR or operations?",
    "How do I measure whether Speak Your Menu is working?",
    "Have restaurants seen real results with Speak Your Menu?",
    "How does Speak Your Menu help general managers and directors of operations?",
    "Can back-of-house, hosts, or support staff use Speak Your Menu?",
  ],
  "Integrations & Tech": [
    "How is our restaurant data stored and protected?",
    "Does Speak Your Menu integrate with POS systems?",
    "Can Speak Your Menu automatically import my POS menu?",
  ],
  "Support & Roadmap": [
    "Does this app teach foreign menu terms like Italian or French?",
    "Will you support wine pairings in the future?",
    "Will you support other beverages like beer or cocktails in the future?",
    "Does Speak Your Menu support multiple languages?",
    "Do you offer onboarding support?",
    "What kind of ongoing support do you provide?",
    "Do you provide best practices for rolling out Speak Your Menu?",
  ],
};

const FAQ_CATEGORY_LOOKUP = new Map(
  FAQ_CATEGORIES.flatMap((category) =>
    FAQ_CATEGORY_QUESTIONS[category].map((question) => [question, category])
  )
);

const faqItemsWithCategories = faqData.map((faq) => ({
  ...faq,
  category: FAQ_CATEGORY_LOOKUP.get(faq.question) || "Support & Roadmap",
}));

function normalizeAccordionKeys(eventKey) {
  if (Array.isArray(eventKey)) return eventKey;
  if (eventKey === null || eventKey === undefined) return [];
  return [eventKey];
}

function renderFaqAnswer(faq) {
  if (!shouldShowContentClusterPromos()) {
    return faq.answer;
  }

  if (faq.question === "How does this app make learning the menu easier?") {
    return (
      <>
        {faq.answer}{" "}
        <Link to={MENU_TRAINING_PILLAR_PATH}>Read the full menu training guide</Link>
        {" "}for the operating model behind that approach.
      </>
    );
  }

  if (faq.question === "How does the app help streamline the onboarding process?") {
    return (
      <>
        {faq.answer}{" "}
        <Link to={SERVER_TRAINING_CHECKLIST_PATH}>Use the onboarding checklist</Link>
        {" "}if you want a shorter rollout version for managers.
      </>
    );
  }

  if (faq.question === "Is Speak Your Menu good for restaurants with high staff turnover?") {
    return (
      <>
        {faq.answer}{" "}
        <Link to={SERVER_TRAINING_PILLAR_PATH}>The server-training guide</Link>
        {" "}shows how to keep onboarding and early-shift standards consistent even when staffing changes quickly.
      </>
    );
  }

  if (faq.question === "How does the app handle beverage education for wine specifically?") {
    return (
      <>
        {faq.answer}{" "}
        <Link to={WINE_TRAINING_PILLAR_PATH}>Read the wine-training guide</Link>
        {" "}for the operating model behind stronger wine language, list confidence, and recommendation quality.
      </>
    );
  }

  if (faq.question === "What learning techniques does it use?") {
    return (
      <>
        {faq.answer}{" "}
        <Link to={MENU_TRAINING_PILLAR_PATH}>See the full system</Link>
        {" "}for how repetition, recall, and manager visibility work together.
      </>
    );
  }

  if (faq.question === "How quickly can I expect staff to become fluent in our menu?") {
    return (
      <>
        {faq.answer}{" "}
        <Link to={SERVER_TRAINING_PILLAR_PATH}>Read the server-training guide</Link>
        {" "}for a first-week rollout model that improves confidence faster.
      </>
    );
  }

  if (faq.question === "How does Speak Your Menu help with allergy safety?") {
    return (
      <>
        {faq.answer}{" "}
        <Link to={ALLERGEN_SAFETY_PILLAR_PATH}>Read the allergen-safety guide</Link>
        {" "}for the operating model behind better dietary answers and safer service habits.
      </>
    );
  }

  if (faq.question === "Does Speak Your Menu help staff answer detailed allergy questions at the table?") {
    return (
      <>
        {faq.answer}{" "}
        <Link to={ALLERGEN_SAFETY_QA_GUIDE_PATH}>Use the allergen Q&amp;A guide</Link>
        {" "}if you want a shorter table-side reference for managers and staff.
      </>
    );
  }

  return faq.answer;
}

function FAQ() {
  const showContentClusterPromos = shouldShowContentClusterPromos();
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [expandedItemsByCategory, setExpandedItemsByCategory] = useState({});

  const normalizedSearch = search.trim().toLowerCase();

  const visibleCategorySections = useMemo(
    () =>
      FAQ_CATEGORIES.map((category) => ({
        category,
        items: faqItemsWithCategories.filter(({ category: faqCategory, question, answer }) => {
          if (faqCategory !== category) return false;
          if (selectedCategory !== "All" && selectedCategory !== category) return false;
          if (!normalizedSearch) return true;

          return (
            question.toLowerCase().includes(normalizedSearch) ||
            answer.toLowerCase().includes(normalizedSearch)
          );
        }),
      })).filter(({ items }) => items.length > 0),
    [normalizedSearch, selectedCategory]
  );

  const visibleQuestionCount = useMemo(
    () => visibleCategorySections.reduce((total, section) => total + section.items.length, 0),
    [visibleCategorySections]
  );

  const searchSummary = normalizedSearch
    ? `${visibleQuestionCount} result${visibleQuestionCount === 1 ? "" : "s"}${selectedCategory === "All" ? "" : ` in ${selectedCategory}`}`
    : `${faqData.length} questions across ${FAQ_CATEGORIES.length} categories`;

  const setCategoryExpandedItems = (category, questions) => {
    setExpandedItemsByCategory((current) => ({
      ...current,
      [category]: questions,
    }));
  };

  const handleAccordionSelect = (category, eventKey) => {
    setCategoryExpandedItems(category, normalizeAccordionKeys(eventKey));
  };

  const handleExpandAll = (category, items) => {
    setCategoryExpandedItems(
      category,
      items.map(({ question }) => question)
    );
  };

  const handleCollapseAll = (category) => {
    setCategoryExpandedItems(category, []);
  };

  // Generate FAQPage structured data for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>FAQ | Restaurant Staff Training & Menu Education</title>
        <meta
          name="description"
          content="Frequently asked questions about restaurant staff training, menu education, and front-of-house development. Learn how Speak Your Menu helps restaurant owners, managers, and servers train faster and sell more."
        />
        {/* Open Graph */}
        <meta
          property="og:title"
          content="FAQ | Restaurant Staff Training & Menu Education"
        />
        <meta
          property="og:description"
          content="Frequently asked questions about restaurant staff training, menu education, and front-of-house development. Learn how Speak Your Menu helps restaurant owners, managers, and servers train faster and sell more."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://speakyourmenu.com/faq" />
        <meta
          property="og:image"
          content="https://speakyourmenu.com/SpeakYourMenu_Logo.png"
        />
        <meta property="og:site_name" content="Speak Your Menu" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="FAQ | Restaurant Staff Training & Menu Education"
        />
        <meta
          name="twitter:description"
          content="Frequently asked questions about restaurant staff training, menu education, and front-of-house development. Learn how Speak Your Menu helps restaurant owners, managers, and servers train faster and sell more."
        />
        <meta
          name="twitter:image"
          content="https://speakyourmenu.com/SpeakYourMenu_Logo.png"
        />
        <meta name="twitter:site" content="@speakyourmenu" />
        {/* Canonical URL */}
        <link rel="canonical" href="https://speakyourmenu.com/faq" />
        {/* BreadcrumbList JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://speakyourmenu.com/"
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "FAQ",
                item: "https://speakyourmenu.com/faq"
              }
            ]
          })}
        </script>
        {/* FAQPage JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="faq-page homepage-redesign">
        <section className="homepage-section homepage-hero-section faq-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="faq-hero-orb faq-hero-orb-left" aria-hidden="true" />
          <div className="faq-hero-orb faq-hero-orb-right" aria-hidden="true" />

          <div className="homepage-shell faq-hero-shell">
            <div className="faq-hero-copy">
              <h1 className="homepage-hero-title faq-hero-title">Frequently Asked Questions</h1>
              <p className="homepage-hero-subhead faq-hero-subhead">
                Find quick answers about setup, pricing, mobile apps, and multi-location.
              </p>
            </div>
          </div>

          <div className="homepage-shell faq-search-shell">
            <aside className="homepage-surface-card faq-search-card" aria-label="FAQ search and filters">
              <div className="faq-search-card-header">
                <p className="faq-search-kicker">Search and browse</p>
                <p className="faq-search-summary">{searchSummary}</p>
              </div>

              <Form className="faq-search-form" role="search" aria-label="FAQ search">
                <label htmlFor="faq-search-input" className="visually-hidden">
                  Search FAQs
                </label>
                <div className="faq-search-input-wrap">
                  <Form.Control
                    id="faq-search-input"
                    type="search"
                    placeholder="Search by question or answer"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    aria-label="Search FAQs"
                    className="form-control-lg faq-search-input"
                  />
                  {search ? (
                    <button
                      type="button"
                      className="faq-search-clear"
                      onClick={() => setSearch("")}
                      aria-label="Clear FAQ search"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              </Form>

              <div className="faq-chip-bar" aria-label="FAQ categories" role="group">
                {["All", ...FAQ_CATEGORIES].map((category) => {
                  const isActive = selectedCategory === category;

                  return (
                    <button
                      key={category}
                      type="button"
                      aria-pressed={isActive}
                      className={`faq-chip${isActive ? " faq-chip-active" : ""}`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category}
                    </button>
                  );
                })}
              </div>

              <p className="faq-search-note">Search matches both question titles and answer copy.</p>
            </aside>
          </div>
        </section>

        <section className="homepage-section faq-categories-section">
          <div className="homepage-shell faq-category-stack">
            {showContentClusterPromos ? (
              <section className="homepage-surface-card resource-card faq-resource-card" aria-label="Featured training resources">
                <div className="resource-grid-two faq-resource-grid resource-grid-two-stackable">
                  <div>
                    <span className="homepage-label-chip">Featured resources</span>
                    <h2 className="homepage-section-title resource-section-title">Need the practical menu training version?</h2>
                    <p className="homepage-card-copy resource-section-body">
                      If you are here because you need a clearer system for onboarding and menu knowledge, start with the full guide and keep the checklist nearby for rollout.
                    </p>
                  </div>
                  <div className="resource-inline-actions faq-resource-actions">
                    <Link to={MENU_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                    <Link to={MENU_TRAINING_TEMPLATE_PATH} className="resource-secondary-link">Get template</Link>
                    <Link to={MENU_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open checklist</Link>
                  </div>
                  <div>
                    <h2 className="homepage-section-title resource-section-title">Need the server onboarding version?</h2>
                    <p className="homepage-card-copy resource-section-body">
                      If your main issue is getting new servers confident faster, use the server-training guide and onboarding checklist instead.
                    </p>
                  </div>
                  <div className="resource-inline-actions faq-resource-actions">
                    <Link to={SERVER_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                    <Link to={SERVER_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open onboarding checklist</Link>
                  </div>
                  <div>
                    <h2 className="homepage-section-title resource-section-title">Need the wine sales and pairing version?</h2>
                    <p className="homepage-card-copy resource-section-body">
                      If beverage confidence is the real problem, use the wine-training guide and pairing guide to improve recommendations and guest-facing wine language.
                    </p>
                  </div>
                  <div className="resource-inline-actions faq-resource-actions">
                    <Link to={WINE_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                    <Link to={WINE_TRAINING_PAIRING_GUIDE_PATH} className="resource-secondary-link">Open pairing guide</Link>
                  </div>
                  <div>
                    <h2 className="homepage-section-title resource-section-title">Need the allergen and dietary safety version?</h2>
                    <p className="homepage-card-copy resource-section-body">
                      If the main concern is ingredient accuracy, substitutions, and safer allergy answers, use the allergen-safety guide and Q&amp;A guide.
                    </p>
                  </div>
                  <div className="resource-inline-actions faq-resource-actions">
                    <Link to={ALLERGEN_SAFETY_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                    <Link to={ALLERGEN_SAFETY_QA_GUIDE_PATH} className="resource-secondary-link">Open Q&amp;A guide</Link>
                  </div>
                </div>
              </section>
            ) : null}
            {visibleCategorySections.length === 0 ? (
              <div className="homepage-surface-card faq-empty-state" role="status">
                <span className="homepage-label-chip">No Matches</span>
                <h2 className="homepage-section-title faq-empty-title">No questions matched your filters.</h2>
                <p className="homepage-card-copy faq-empty-body">
                  Try a broader search term or switch back to All categories.
                </p>
              </div>
            ) : (
              visibleCategorySections.map(({ category, items }, index) => {
                const expandedQuestions = expandedItemsByCategory[category] || [];
                const allExpanded = items.length > 0 && items.every(({ question }) => expandedQuestions.includes(question));
                const allCollapsed = items.every(({ question }) => !expandedQuestions.includes(question));

                return (
                  <section
                    key={category}
                    className={`faq-category-block${index % 2 === 1 ? " faq-category-block-alt" : ""}`}
                    aria-labelledby={`faq-category-${category}`}
                  >
                    <article className="homepage-surface-card faq-category-card">
                      <div className="faq-category-header">
                        <div>
                          <p className="faq-category-eyebrow">Category</p>
                          <h2 id={`faq-category-${category}`} className="faq-category-title">
                            {category}
                          </h2>
                        </div>
                        <div className="faq-category-meta">
                          <span className="faq-category-count">
                            {items.length} question{items.length === 1 ? "" : "s"}
                          </span>
                          <div className="faq-category-actions" aria-label={`${category} controls`}>
                            <button
                              type="button"
                              className="btn btn-wine-primary faq-category-action faq-category-action-primary"
                              onClick={() => handleExpandAll(category, items)}
                              disabled={allExpanded}
                            >
                              Expand all
                            </button>
                            <button
                              type="button"
                              className="btn btn-wine-fill faq-category-action faq-category-action-secondary"
                              onClick={() => handleCollapseAll(category)}
                              disabled={allCollapsed}
                            >
                              Collapse all
                            </button>
                          </div>
                        </div>
                      </div>

                      <Accordion
                        alwaysOpen
                        activeKey={expandedQuestions}
                        onSelect={(eventKey) => handleAccordionSelect(category, eventKey)}
                        className="faq-accordion"
                      >
                        {items.map((faq) => (
                          <Accordion.Item eventKey={faq.question} key={faq.question}>
                            <Accordion.Header>{faq.question}</Accordion.Header>
                            <Accordion.Body>
                              <span className="faq-answer-copy">{renderFaqAnswer(faq)}</span>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </article>
                  </section>
                );
              })
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default FAQ;